import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE, absoluteUrl } from '../config/site';

// Reusable per-page SEO head: title, description, canonical, Open Graph,
// Twitter card, and an optional JSON-LD @graph built from schema nodes.
//
// Props:
//   title        page title (brand suffix appended unless noBrandSuffix)
//   description  meta description (defaults to site description)
//   path         site-relative path for canonical/OG url (default '/')
//   canonical    explicit canonical path override (else uses `path`)
//   image        OG/Twitter image (site-relative or absolute)
//   type         og:type (default 'website')
//   schemas      array of schema.org nodes (see utils/structuredData)
//   noindex      emit robots noindex,follow (e.g. not-found pages)
const Seo = ({
  title,
  description = SITE.description,
  path = '/',
  canonical,
  image = SITE.ogImage,
  type = 'website',
  schemas,
  noindex = false,
  noBrandSuffix = false,
}) => {
  const fullTitle = !title
    ? SITE.defaultTitle
    : noBrandSuffix
      ? title
      : `${title} | ${SITE.name}`;

  const url = absoluteUrl(canonical || path);
  const imageUrl = absoluteUrl(image);
  const graph =
    schemas && schemas.length
      ? { '@context': 'https://schema.org', '@graph': schemas }
      : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {!noindex && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {graph && (
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
